/* eslint-disable max-lines */
import ModalClass from "common/utils/ui/modal-class";
import EventClass from "common/utils/ui/event-class";
import initFavoriteIcon from "components/jiffy/line-item-favorite-icon/line-item-favorite-icon";
import initDeliveryDateSimple from "components/jiffy/product-details-delivery-date-simple/product-details-delivery-date-simple";
import { BuyItAgainDtfBuy } from "./buy-it-again-dtf-buy";
import BuyItAgainDtfPriceController from "./buy-it-again-dtf-price-controller";
import "./buy-it-again-dtf-modal.scss";

const DTF_MIN = 1;
const DTF_MAX_WIDTH = 22;
const SHEET_MAX_HEIGHT = 120;
const GANG_MAX_HEIGHT = 240;
// Matches PDP MAX_SIZE_FIELD_VALUE — caps a single typed edge so the
// proportional partner can't blow up to absurd values during transient typing.
const MAX_SIZE_FIELD_VALUE = 300;

export class BuyItAgainDtfModal extends ModalClass {
  #submit;

  #qtyInputEvent;

  #decreaseEvent;

  #increaseEvent;

  #originalWidth = null;

  #originalHeight = null;

  #aspectRatio = null;

  #isDtfGang = false;

  #productId = null;

  #dtfArtworkId = null;

  #variantId = null;

  #priceController;

  constructor(options) {
    super(options);

    this.#initEvents(options.modalSelector);
    this.#submit = new BuyItAgainDtfBuy({
      eventSelector: `${options.modalSelector} .js-submit`,
      getQty: () => this.#qtyValue,
      getDimensions: () => this.#dimensionsForSubmit(),
      eventCallback: this.closeModal.bind(this)
    });
    this.#priceController = new BuyItAgainDtfPriceController({
      getModal: () => this.$modal,
      getQty: () => this.#qtyValue
    });
  }

  get #qtyValue() {
    return parseInt(this.$modal.querySelector(".js-qty").value, 10) || 1;
  }

  set #qtyValue(value) {
    this.$modal.querySelector(".js-qty").value = Math.max(1, value);
  }

  #widthValue() {
    const v = parseFloat(this.$modal.querySelector(".js-width-input").value);
    return Number.isFinite(v) ? v : null;
  }

  #heightValue() {
    const v = parseFloat(this.$modal.querySelector(".js-height-input").value);
    return Number.isFinite(v) ? v : null;
  }

  // Returns { width, height } when the user has changed the size, or null when
  // the inputs match the original dtf_print dimensions. Used by BuyItAgainDtfBuy
  // to decide whether to include width/height in the print_reorder payload.
  #dimensionsForSubmit() {
    const w = this.#widthValue();
    const h = this.#heightValue();
    if (w === null || h === null) return null;
    if (w === this.#originalWidth && h === this.#originalHeight) return null;
    return { width: w, height: h };
  }

  #validateDimensions() {
    const w = this.#widthValue();
    const h = this.#heightValue();
    if (w === null || h === null) {
      return { ok: false, msg: "Enter width and height" };
    }
    if (w < DTF_MIN || h < DTF_MIN) {
      return { ok: false, msg: `Minimum size is ${DTF_MIN}″ × ${DTF_MIN}″` };
    }
    const maxH = this.#isDtfGang ? GANG_MAX_HEIGHT : SHEET_MAX_HEIGHT;
    const portraitOk = w <= DTF_MAX_WIDTH && h <= maxH;
    const landscapeOk = w <= maxH && h <= DTF_MAX_WIDTH;
    if (!portraitOk && !landscapeOk) {
      return { ok: false, msg: `Maximum size is ${DTF_MAX_WIDTH}″ × ${maxH}″` };
    }
    return { ok: true, msg: null };
  }

  #renderValidation({ ok, msg }) {
    const $err = this.$modal.querySelector(".js-dim-error");
    const $submit = this.$modal.querySelector(".js-submit");
    if ($err) {
      if (ok) {
        $err.hidden = true;
        $err.textContent = "";
      } else {
        $err.hidden = false;
        $err.textContent = msg;
      }
    }
    if ($submit) {
      if (ok) {
        $submit.removeAttribute("disabled");
      } else {
        $submit.setAttribute("disabled", "disabled");
      }
    }
  }

  // Mirrors PDP ArtworkDimensions.vue.tsx#getProportionalEdge — given a typed
  // edge, returns the locked partner edge so width:height stays at the source
  // artwork's aspect ratio. Returns null if we can't lock (no ratio, or input
  // not a finite number) and the caller should leave the partner alone.
  #getProportionalEdge(value, partner) {
    if (!this.#aspectRatio || !Number.isFinite(value)) return null;
    const factor = partner === "height" ? 1 / this.#aspectRatio : this.#aspectRatio;
    return Math.round(value * factor * 100) / 100;
  }

  #onWidthInput() {
    const $width = this.$modal.querySelector(".js-width-input");
    const $height = this.$modal.querySelector(".js-height-input");
    let typed = parseFloat($width.value);
    if (Number.isFinite(typed) && typed > MAX_SIZE_FIELD_VALUE) {
      typed = MAX_SIZE_FIELD_VALUE;
      $width.value = String(typed);
    }
    const partner = this.#getProportionalEdge(typed, "height");
    if (partner !== null) $height.value = partner.toFixed(2);
    this.#afterDimensionChange();
  }

  #onHeightInput() {
    const $width = this.$modal.querySelector(".js-width-input");
    const $height = this.$modal.querySelector(".js-height-input");
    let typed = parseFloat($height.value);
    if (Number.isFinite(typed) && typed > MAX_SIZE_FIELD_VALUE) {
      typed = MAX_SIZE_FIELD_VALUE;
      $height.value = String(typed);
    }
    const partner = this.#getProportionalEdge(typed, "width");
    if (partner !== null) $width.value = partner.toFixed(2);
    this.#afterDimensionChange();
  }

  #afterDimensionChange() {
    const result = this.#validateDimensions();
    this.#renderValidation(result);
    if (result.ok) {
      this.#priceController.updateDimensions(this.#widthValue(), this.#heightValue());
      this.#priceController.scheduleFetch();
    } else {
      this.#priceController.abort();
    }
  }

  #fillModal(payload) {
    this.$modal
      .querySelectorAll(".js-link")
      .forEach($link => $link.setAttribute("href", payload.productUrl));
    this.$modal.querySelector(".js-brand-url").setAttribute("href", payload.brandUrl);
    this.$modal.querySelector(".js-brand-logo").setAttribute("src", payload.brandLogo);
    this.$modal.querySelector(".js-preview").setAttribute("src", payload.preview);
    this.$modal.querySelector(".js-title").textContent = payload.productName;
    this.$modal.querySelector(".js-width").textContent = payload.width;
    this.$modal.querySelector(".js-height").textContent = payload.height;
    // Number inputs reject non-numeric values via HTML5 sanitization, so seed
    // them with the parsed numerics — payload.width is "5.2”" (formatted with
    // the inch mark) and would silently blank out a type="number" input.
    this.$modal.querySelector(".js-width-input").value = Number.isFinite(payload.widthValue)
      ? payload.widthValue
      : "";
    this.$modal.querySelector(".js-height-input").value = Number.isFinite(payload.heightValue)
      ? payload.heightValue
      : "";
    this.$modal.querySelector(".js-catalog-number").textContent = payload.catalogNumber;
    this.#qtyValue = 1;
    this.#originalWidth = Number.isFinite(payload.widthValue) ? payload.widthValue : null;
    this.#originalHeight = Number.isFinite(payload.heightValue) ? payload.heightValue : null;
    // Aspect ratio is locked to the source artwork's print dims (which mirror
    // its source aspect — DTF prints don't distort). Same convention as PDP:
    // ratio = width / height, factor = 1/ratio when computing height-from-width.
    this.#aspectRatio =
      this.#originalWidth && this.#originalHeight
        ? this.#originalWidth / this.#originalHeight
        : null;
    this.#isDtfGang = !!payload.isDtfGang;
    this.#renderValidation(this.#validateDimensions());
  }

  #onQtyChange() {
    this.#handleDecreaseButtonState();
    this.#priceController.scheduleFetch();
  }

  #inputQty() {
    this.#qtyValue += 0; // ensure qtyValue is at least 1
    this.#onQtyChange();
  }

  #decreaseQty() {
    this.#qtyValue -= 1;
    this.#onQtyChange();
  }

  #increaseQty() {
    this.#qtyValue += 1;
    this.#onQtyChange();
  }

  #handleDecreaseButtonState() {
    const $decreaseButton = this.$modal.querySelector(".js-decrease");

    if (this.#qtyValue <= 1) {
      $decreaseButton.setAttribute("disabled", "disabled");
    } else {
      $decreaseButton.removeAttribute("disabled");
    }
  }

  #initEvents(wrapperSelector) {
    this.#qtyInputEvent = new EventClass({
      eventSelector: `${wrapperSelector} .js-qty`,
      event: "blur",
      eventCallback: this.#inputQty.bind(this),
      eventOptions: { passive: true }
    });

    this.#decreaseEvent = new EventClass({
      eventSelector: `${wrapperSelector} .js-decrease`,
      event: "click",
      eventCallback: this.#decreaseQty.bind(this),
      eventOptions: { passive: true }
    });

    this.#increaseEvent = new EventClass({
      eventSelector: `${wrapperSelector} .js-increase`,
      event: "click",
      eventCallback: this.#increaseQty.bind(this),
      eventOptions: { passive: true }
    });

    // Direct binding (not document delegation) for the size inputs so each
    // modal instance only reacts to its own inputs. This avoids cross-instance
    // handler accumulation and works reliably under JSDOM in tests.
    // Per-input handlers so we know which edge the user typed in and can
    // proportionally update the other.
    // The modal is constructed eagerly on every account page load — including
    // pages that don't render the modal partial — so guard against `this.$modal`
    // being null. Without this, the throw bubbles out of the bundle's
    // DOMContentLoaded handler and skips later inits (e.g. the masthead dropdown).
    const $widthInput = this.$modal?.querySelector(".js-width-input");
    const $heightInput = this.$modal?.querySelector(".js-height-input");
    if ($widthInput) $widthInput.addEventListener("input", this.#onWidthInput.bind(this));
    if ($heightInput) $heightInput.addEventListener("input", this.#onHeightInput.bind(this));
  }

  #initFavorite(favorite) {
    const $iconOld = this.$modal.querySelector(".js-favorite:not(.hidden)");
    $iconOld?.remove();

    const $iconTemplate = this.$modal.querySelector(".js-favorite.hidden");
    const $icon = $iconTemplate.cloneNode(true);
    $iconTemplate.parentNode.insertBefore($icon, $iconTemplate);

    $icon.classList.toggle("line-item-favorite-icon--added", favorite.is_favorited);
    $icon.classList.add("js-line-item-favorite-icon");
    $icon.classList.remove("hidden");

    $icon.dataset.favoriteColor = favorite.favorite_color;
    $icon.dataset.favoriteCompositionId = favorite.favorite_composition_id;
    $icon.dataset.isFavorited = favorite.is_favorited;
    $icon.dataset.productId = favorite.product_id;
    $icon.dataset.productStyle = favorite.product_style;
    $icon.dataset.brand = favorite.brand;
    $icon.dataset.productName = favorite.product_name;
    $icon.dataset.compositionInventoryRank = favorite.composition_inventory_rank;
    $icon.dataset.shippingRule = favorite.shipping_rule;
    $icon.dataset.adwordsCategory = favorite.adwords_category;
    $icon.dataset.buttonDisplayType = favorite.button_display_type;
    $icon.dataset.businessLine = favorite.business_line;
    $icon.dataset.businessLineId = favorite.business_line_id;

    initFavoriteIcon();
  }

  async #loadDeliveryDate(productUrl, lineItemId, lineItemSlug) {
    try {
      const $deliveryDateContainer = this.$modal.querySelector(
        "#js-product-details-delivery-dates-simple-container"
      );
      if ($deliveryDateContainer) {
        const productSlug = lineItemSlug || String(lineItemId).replace(/\.html$/, "");
        $deliveryDateContainer.dataset.productId = productSlug;
        $deliveryDateContainer.dataset.selectedColor = "Transfer";
        $deliveryDateContainer.dataset.assignable = "true";
        $deliveryDateContainer.dataset.isDtf = "true";
        await initDeliveryDateSimple();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load delivery date:", error);
    }
  }

  openModal({ favorite, ...data }) {
    super.openModal();

    this.#submit.variantId = data.variantId;
    this.#submit.dtfPrintId = data.dtfPrintId;
    this.#submit.productId = data.lineItemId;
    this.#productId = data.lineItemId;
    this.#dtfArtworkId = data.dtfArtworkId;
    this.#variantId = data.variantId;

    this.#priceController.configure({
      dtfPrintId: data.dtfPrintId ?? null,
      lineItemId: data.lineItemId ?? null,
      enhancementTier: data.enhancementTier ?? null,
      preCut: data.preCut === true || data.preCut === "true",
      widthValue: data.widthValue != null ? Number(data.widthValue) : null,
      heightValue: data.heightValue != null ? Number(data.heightValue) : null
    });

    this.#fillModal(data);
    this.#initFavorite(favorite);

    if (data.productUrl && data.lineItemId) {
      this.#loadDeliveryDate(data.productUrl, data.lineItemId, data.lineItemSlug);
    }

    this.#priceController.fetch();
  }

  closeModalCallback() {
    this.#priceController.abort();
    return super.closeModalCallback();
  }
}

export default props =>
  new BuyItAgainDtfModal({
    modalSelector: "#buy-it-again-dtf-modal",
    ...props
  });
